/**
 * Lead bar (domain / email submit) → Slack via Incoming Webhook.
 * Browser + CORS proxy: works on CRA localhost (Slack blocks direct browser POSTs).
 * Uses REACT_APP_SLACK_ONBOARDING_WEBHOOK_URL (same as onboarding notifications).
 */

const CORS_PROXY = 'https://mycorsproxy-tuto-35940f676099.herokuapp.com/';

function slackSafe(text, maxLen = 900) {
	if (text == null || text === '') return '—';
	let s = String(text);
	if (s.length > maxLen) {
		s = `${s.slice(0, Math.max(0, maxLen - 1))}…`;
	}
	return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function buildSlackPayload(body) {
	const {
		rawInput,
		extractedEmail,
		normalizedDomain,
		outcome,
		clerkEmail,
		clerkName,
		pageUrl,
		errorMessage,
		details,
	} = body || {};

	const outcomeLabels = {
		existing_company_redirect: 'Redirect → existing company',
		new_enrich_redirect: 'New domain → Clay + summary',
		validation_error: 'Validation failed',
		unexpected_error: 'Unexpected error',
	};

	const who =
		clerkEmail || clerkName
			? [clerkName, clerkEmail].filter(Boolean).join(' · ')
			: 'Not signed in';

	const blocks = [
		{
			type: 'header',
			text: { type: 'plain_text', text: '🎯 Lead bar submission' },
		},
		{
			type: 'section',
			fields: [
				{ type: 'mrkdwn', text: `*Raw input*\n${slackSafe(rawInput)}` },
				{
					type: 'mrkdwn',
					text: `*Outcome*\n${slackSafe(outcomeLabels[outcome] || outcome || '—', 200)}`,
				},
				{ type: 'mrkdwn', text: `*Email from field*\n${slackSafe(extractedEmail)}` },
				{ type: 'mrkdwn', text: `*Normalized domain*\n${slackSafe(normalizedDomain)}` },
				{ type: 'mrkdwn', text: `*Signed-in user*\n${slackSafe(who)}` },
				{ type: 'mrkdwn', text: `*Page*\n${slackSafe(pageUrl)}` },
			],
		},
	];

	if (details) {
		blocks.push({
			type: 'section',
			text: { type: 'mrkdwn', text: `*Details*\n${slackSafe(details)}` },
		});
	}

	if (errorMessage) {
		blocks.push({
			type: 'section',
			text: { type: 'mrkdwn', text: `*Error*\n${slackSafe(errorMessage)}` },
		});
	}

	blocks.push({
		type: 'context',
		elements: [{ type: 'mrkdwn', text: `Submitted at ${new Date().toISOString()}` }],
	});

	return {
		text: `Lead bar: ${slackSafe(rawInput || normalizedDomain || 'submission', 250)}`,
		blocks,
	};
}

/**
 * @param {Record<string, unknown>} partial payload fields
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function postLeadBarSlackFromBrowser(partial, options = {}) {
	const webhookUrl = process.env.REACT_APP_SLACK_ONBOARDING_WEBHOOK_URL;
	if (!webhookUrl) {
		console.warn(
			'[Lead bar] REACT_APP_SLACK_ONBOARDING_WEBHOOK_URL is not set — add it to .env and restart the dev server.'
		);
		return;
	}

	const fullUrl = `${CORS_PROXY}${webhookUrl}`;
	const payload = JSON.stringify(buildSlackPayload(partial));
	const fetchOpts = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Access-Control-Allow-Origin': '*',
			'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
		},
		body: payload,
		keepalive: true,
	};
	if (options.signal) {
		fetchOpts.signal = options.signal;
	}

	const res = await fetch(fullUrl, fetchOpts);

	if (!res.ok) {
		const t = await res.text().catch(() => '');
		console.warn('[Lead bar] Slack webhook failed', res.status, t);
	}
}
